import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import EmployeeTable from "../components/employee/EmployeeTable";
import { useStores } from "../../use-stores";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { GetEmployeeParams } from "../../../../models/Employees/GetEmployeeParams";
import { SortType } from "../../../../models/SortType";
import ConfirmPopup from "../../../../views/components/common/pop-up/ConfirmPopup";
import { useNotification } from "../../../../utils/NotificationsContext";
import FormTextField from "../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../views/components/common/form/CustomTypography";
import AppDropDown from "../../../../views/components/common/filter/AppDropDown";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { useDebounce } from "../../../../views/components/common/hooks/Debounce";

const SEARCH_DEBOUNCE_MS = 400;

const employeeStatusFilterOptions: FilterValue[] = [
  { id: "all", value: "Select" },
  { id: "true", value: "Active" },
  { id: "false", value: "Inactive" },
];

const EmployeeContent: React.FC = () => {
  const { employeeStore, userStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);

  const initialParams: GetEmployeeParams = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sortBy: searchParams.get("sort_by") || "name",
    sortType:
      searchParams.get("sort_type") === "ASC" ? SortType.DESC : SortType.ASC,
    search_text: searchParams.get("search_text") || "",
    active: searchParams.get("active")
      ? searchParams
        .get("active")!
        .split(",")
          .map((value) => {
            if (value === "true") return 1;
            if (value === "false") return 0;
            const num = parseInt(value, 10);
            return Number.isNaN(num) ? undefined : num;
          })
          .filter((value): value is number => value !== undefined)
      : undefined,
  };

  const [params, setParams] = useState(initialParams);
  const [selectedStatusId, setSelectedStatusId] = useState<string>(() => {
    const active = initialParams.active?.[0];
    if (active === 1) return "true";
    if (active === 0) return "false";
    return "all";
  });
  const [searchTerm, setSearchTerm] = useState<string>(
    initialParams.search_text || "",
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const listRequestId = useRef(0);

  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const theme = useTheme();
  const { showError } = useNotification();

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    setParams((prev) => {
      if ((prev.search_text || "") === trimmed) return prev;
      return {
        ...prev,
        search_text: trimmed,
        page_no: 1,
      };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    const requestId = ++listRequestId.current;
    setIsLoading(true);
    employeeStore
      .getEmployee(params)
      .catch((reason) => {
        if (requestId !== listRequestId.current) return;
        showError(
          "Error!",
          reason?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        if (requestId !== listRequestId.current) return;
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, employeeStore]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
  }, [params, navigate]);

  const handleSearchBar = (value: string | undefined) => {
    if (value && value.length !== 0) {
      setSearchTerm(value);
    } else {
      setSearchTerm("");
    }
  };

  const handleStatusChange = (id: string) => {
    setSelectedStatusId(id);
    setParams((prev) => {
      const nextActive = id === "all" ? undefined : [id === "true" ? 1 : 0];
      const current = prev.active?.[0];
      const next = nextActive?.[0];
      if (current === next) return prev;
      return {
        ...prev,
        page_no: 1,
        active: nextActive,
      };
    });
  };

  const applyFilters = () => {
    const nextSearch = searchTerm.trim();
    setSearchTerm(nextSearch);
    setParams((prev) => ({
      ...prev,
      search_text: nextSearch,
      page_no: 1,
      active:
        selectedStatusId === "all"
          ? undefined
          : [selectedStatusId === "true" ? 1 : 0],
    }));
  };

  const clearAll = () => {
    setSearchTerm("");
    setSelectedStatusId("all");
    setParams((prev) => ({
      ...prev,
      search_text: "",
      page_no: 1,
      active: undefined,
    }));
  };
  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const openEmployeeScreen = (path: string) => {
    navigate(path, { state: { listingSearch: location.search } });
  };

  const handleDeleteClick = (employeeId: number) => {
    setConfirmDeleteId(employeeId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setIsDeleting(true);
      const res = await employeeStore.deleteEmployee(confirmDeleteId);
      if ((res as any)?.success) {
        await employeeStore.getEmployee(params);
      } else {
        showError(
          "Error!",
          (res as any)?.message ||
          "Failed to delete employee! Please try again later.",
        );
      }
    } catch (err: any) {
      showError(
        "Error!",
        err?.response?.data?.message ||
        "Failed to delete employee! Please make sure you have proper rights to delete other employee",
      );
    } finally {
      setIsDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1,
            }}
          >
            <Box>
              <CustomTypography variant="heading3">Employees</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage employees. Search by name or email.
              </CustomTypography>
            </Box>
            {userStore.isSuperAdminManager() && (
              <AppButton
                variant="primary"
                label="Add Employee"
                onClick={() => openEmployeeScreen("/employee/add")}
                customStyles={{ minWidth: 140, height: 40 }}
              />
            )}
          </Box>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.5,
              cursor: "pointer",
              borderBottom: isSearchExpanded ? "1px solid" : "none",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CustomTypography variant="heading6">Filters</CustomTypography>
            <IconButton size="small">
              {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: 1.5,
                p: { xs: 1.5, sm: 2 },
              }}
            >
              <Box sx={{ flex: { md: 1 }, minWidth: 0, width: "100%" }}>
                <FormTextField
                  id="employee-search"
                  placeholder="Search by employee name or email"
                  type="search"
                  variant="outlined"
                  marginBottom={0}
                  value={searchTerm}
                  onChange={(_, value) => handleSearchBar(value)}
                  sx={{
                    "& .MuiOutlinedInput-root": { height: 40 },
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", md: 220 }, flexShrink: 0 }}>
                <AppDropDown
                  title="Status"
                  isMultiSelect={false}
                  value={selectedStatusId}
                  list={employeeStatusFilterOptions}
                  fontSize="12px"
                  width="100%"
                  height="40px"
                  onSingleSelectionChange={handleStatusChange}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  flexShrink: 0,
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <AppButton
                  variant="primary"
                  label="Apply"
                  onClick={applyFilters}
                  customStyles={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 120 },
                    height: 40,
                  }}
                />
                <AppButton
                  variant="transparentPrimary"
                  label="Clear All"
                  onClick={clearAll}
                  customStyles={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 120 },
                    height: 40,
                  }}
                />
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {isLoading && (
          <Container
            sx={{
              minHeight: "40vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Container>
        )}

        {!isLoading && (
          <Box mt={3}>
            {employeeStore.employees.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10rem"
                textAlign="center"
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={500}
                  color={theme.palette.customColors?.greyTextColor}
                >
                  No employees match the current search criteria.
                </CustomTypography>
              </Box>
            )}
            {employeeStore.employees.length > 0 && (
              <EmployeeTable
                employees={employeeStore.employees || []}
                limit={params.limit || employeeStore.limit}
                page_no={params.page_no}
                total_count={employeeStore.total_count}
                total_pages={employeeStore.total_pages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onEditClick={(employeeId) =>
                  openEmployeeScreen(`/employee/${employeeId}`)
                }
                onViewClick={(employeeId) =>
                  openEmployeeScreen(`/employee/${employeeId}`)
                }
                onDeleteClick={handleDeleteClick}
              />
            )}
          </Box>
        )}
      </Paper>

      <ConfirmPopup
        open={confirmDeleteId !== null}
        title="Delete employee?"
        message="This action will permanently remove the employee. Do you want to proceed?"
        cancelText="Cancel"
        confirmText="Apply"
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />
    </Grid2>
  );
};

export default observer(EmployeeContent);
